import React from "react"
import { Link } from "react-router-dom"
import Back from "../common/back/Back"
import "./features.css"

const platforms = [
  {
    icon: "fa fa-chart-bar",
    name: "MetaTrader 4 (MT4)",
    desc: "The world's most popular forex trading platform. Open a free demo account with any regulated broker — practice with virtual money in real market conditions.",
    badge: "Most Popular",
    badgeColor: "#c9a227",
    link: "https://www.metatrader4.com/",
    linkLabel: "Download MT4",
  },
  {
    icon: "fa fa-chart-line",
    name: "MetaTrader 5 (MT5)",
    desc: "The upgraded version of MT4 with more order types, more timeframes, and access to stocks and futures alongside forex. Ideal for intermediate and advanced traders.",
    badge: "Recommended",
    badgeColor: "#2e7d52",
    link: "https://www.metatrader5.com/",
    linkLabel: "Download MT5",
  },
  {
    icon: "fa fa-globe",
    name: "TradingView Paper Trading",
    desc: "Browser-based simulation directly on TradingView charts. No download needed — paper trade on live charts with full technical analysis tools.",
    badge: "Browser-Based",
    badgeColor: "#1a73e8",
    link: "https://www.tradingview.com/",
    linkLabel: "Open TradingView",
  },
  {
    icon: "fa fa-sync-alt",
    name: "Forex Tester",
    desc: "Backtest your strategies on historical data at any speed. Perfect for practicing zone identification and trend analysis without waiting for the market to move.",
    badge: "Backtesting",
    badgeColor: "#6f42c1",
    link: "https://forextester.com/",
    linkLabel: "Try Forex Tester",
  },
]

const steps = [
  { n: "01", title: "Open a Free Demo Account",      desc: "Choose any regulated broker (e.g. XM, Exness, IC Markets) and open a free demo account. You'll receive virtual funds — typically $10,000–$100,000 — to practice with." },
  { n: "02", title: "Set Up Your Charts",            desc: "Apply the same chart setup taught in the Rovers Access programme: correct timeframes, clean charts, no unnecessary indicators. Keep it simple." },
  { n: "03", title: "Identify Zones and Trends",     desc: "Practice identifying demand and supply zones, proximal and distal lines, and trend direction before placing any trade." },
  { n: "04", title: "Plan the Trade First",          desc: "Before clicking Buy or Sell, write down: entry price, stop loss, take profit, and your risk-to-reward ratio. Never enter without a plan." },
  { n: "05", title: "Execute and Journal",           desc: "Execute the trade exactly as planned. Record it in your trading journal: what you saw, why you entered, and what happened." },
  { n: "06", title: "Review with Your Mentor",       desc: "Bring your practice trades to your one-on-one coaching session. Mr. Ali will review your setups, highlight mistakes, and reinforce what you're doing right." },
]

const tips = [
  { icon: "fa fa-exclamation-triangle", color: "#e53e3e", text: "Treat your demo account like real money. Trading carelessly with virtual funds builds bad habits." },
  { icon: "fa fa-check-circle",         color: "#2e7d52", text: "Focus on consistency — not profits. Aim for the same process every trade, not the biggest gains." },
  { icon: "fa fa-check-circle",         color: "#2e7d52", text: "Use proper position sizing from day one — even on demo. Most beginners trade sizes they'd never risk with real money." },
  { icon: "fa fa-check-circle",         color: "#2e7d52", text: "Keep a trading journal from the very first demo trade. The habit of journaling is more valuable than any indicator." },
  { icon: "fa fa-check-circle",         color: "#2e7d52", text: "Only move to a live account when you've demonstrated 3 consecutive weeks of consistent, disciplined trading." },
]

const TradingSimulations = () => (
  <>
    <Back title="Trading Simulations" />

    <section className="feature-page padding">
      <div className="container">

        {/* Intro */}
        <div className="feature-intro">
          <div className="feature-intro-icon" style={{ background: "linear-gradient(135deg,#1a6b9a,#2196f3)" }}>
            <i className="fa fa-laptop-code"></i>
          </div>
          <div className="feature-intro-text">
            <p className="feature-eyebrow">PRACTICE BEFORE YOU RISK</p>
            <h2>Trading Simulations — Build Confidence on Demo</h2>
            <p>
              Before you risk a single shilling of real capital, you should be able to trade
              consistently on a demo (simulation) account. At Rovers Access, we use trading
              simulations as a core part of the learning process — not an optional extra.
            </p>
            <p>
              A demo account lets you practice on <strong>real live market data</strong> using
              virtual money. You'll see real prices, real spreads, and real market movements —
              exactly what you'll face when trading live. The only difference is there's no
              financial risk while you're learning.
            </p>
          </div>
        </div>

        {/* Recommended Platforms */}
        <div className="feature-section">
          <h3 className="feature-section-title"><i className="fa fa-desktop"></i> Recommended Simulation Platforms</h3>
          <div className="sim-platforms-grid">
            {platforms.map((p, i) => (
              <div className="sim-platform-card" key={i}>
                <div className="sim-platform-top">
                  <div className="sim-platform-icon"><i className={p.icon}></i></div>
                  <span className="sim-platform-badge" style={{ background: p.badgeColor }}>{p.badge}</span>
                </div>
                <h4>{p.name}</h4>
                <p>{p.desc}</p>
                <a href={p.link} target="_blank" rel="noreferrer" className="sim-platform-link">
                  <i className="fa fa-external-link-alt"></i> {p.linkLabel}
                </a>
              </div>
            ))}
          </div>
        </div>

        {/* How to use demo */}
        <div className="feature-section">
          <h3 className="feature-section-title"><i className="fa fa-road"></i> How to Use a Demo Account Effectively</h3>
          <div className="cert-steps">
            {steps.map((s, i) => (
              <div className="cert-step" key={i}>
                <div className="cert-step-num">{s.n}</div>
                <div className="cert-step-content">
                  <h4>{s.title}</h4>
                  <p>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tips */}
        <div className="feature-section">
          <h3 className="feature-section-title"><i className="fa fa-lightbulb"></i> Important Tips</h3>
          <div className="feature-benefits">
            {tips.map((t, i) => (
              <div className="feature-benefit-item" key={i}>
                <i className={t.icon} style={{ color: t.color }}></i>
                <span>{t.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* External resources */}
        <div className="feature-section">
          <h3 className="feature-section-title"><i className="fa fa-link"></i> Useful Resources</h3>
          <div className="sim-resources-grid">
            <a href="https://www.forexfactory.com/" target="_blank" rel="noreferrer" className="sim-resource-card">
              <i className="fa fa-calendar-alt"></i>
              <div><strong>Forex Factory</strong><span>Economic calendar & market news</span></div>
              <i className="fa fa-external-link-alt sim-ext-icon"></i>
            </a>
            <a href="https://www.tradingview.com/ideas/" target="_blank" rel="noreferrer" className="sim-resource-card">
              <i className="fa fa-chart-line"></i>
              <div><strong>TradingView Ideas</strong><span>Community trade ideas & analysis</span></div>
              <i className="fa fa-external-link-alt sim-ext-icon"></i>
            </a>
            <a href="https://www.babypips.com/learn/forex" target="_blank" rel="noreferrer" className="sim-resource-card">
              <i className="fa fa-graduation-cap"></i>
              <div><strong>BabyPips School</strong><span>Free forex education for beginners</span></div>
              <i className="fa fa-external-link-alt sim-ext-icon"></i>
            </a>
            <a href="https://www.myfxbook.com/" target="_blank" rel="noreferrer" className="sim-resource-card">
              <i className="fa fa-chart-bar"></i>
              <div><strong>Myfxbook</strong><span>Track and analyse trading performance</span></div>
              <i className="fa fa-external-link-alt sim-ext-icon"></i>
            </a>
          </div>
        </div>

        {/* CTA */}
        <div className="feature-section feature-cta-section">
          <h3>Ready to start practicing?</h3>
          <p>Open a free demo account today, then bring your practice trades to your Rovers Access mentorship session.</p>
          <div className="feature-cta-btns">
            <Link to="/courses" className="feature-btn primary">
              <i className="fa fa-graduation-cap"></i> View Our Programmes
            </Link>
            <a href="https://wa.me/254723000200" target="_blank" rel="noreferrer" className="feature-btn whatsapp">
              <i className="fab fa-whatsapp"></i> Get Started Today
            </a>
          </div>
        </div>

      </div>
    </section>
  </>
)

export default TradingSimulations
